import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { loginSchema } from "@shared/utils/yupSchema";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup.js";
import { CustomTextField } from "@shared/components/CustomTextField";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { login } from "@store/slices/authSlice";

interface MyForm {
  email: string;
  password: string;
}

const SigninForm = () => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state) => state.auth.isLoading);
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MyForm>({
    resolver: yupResolver(loginSchema),
  });

  const submitForm = (data: MyForm) => {
    if (isLoading) return;
    dispatch(login(data));
  };

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(submitForm)}
      noValidate
      spacing={2}
      sx={{ pt: 1 }}
    >
      <CustomTextField
        {...register("email")}
        autoFocus
        autoComplete="email"
        name="email"
        label="Email"
        type="email"
        disabled={isLoading}
        error={!!errors.email}
        helperText={errors.email?.message ?? " "}
        fullWidth
      />
      <CustomTextField
        {...register("password")}
        autoComplete="current-password"
        name="password"
        label="Password"
        type={showPassword ? "text" : "password"}
        disabled={isLoading}
        error={!!errors.password}
        helperText={errors.password?.message ?? " "}
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((v) => !v)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={isLoading}
        startIcon={
          isLoading ? <CircularProgress size={18} color="inherit" /> : null
        }
      >
        {isLoading ? "Logging in…" : "Log in"}
      </Button>
    </Stack>
  );
};

export default SigninForm;
